import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, X, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { ScoreBoard } from "@/components/ScoreBoard";

export const Card = ({ title, children, testid }) => (
  <section data-testid={testid} className="rounded-3xl bg-white shadow-sm border border-slate-200 p-4 space-y-3">
    {title && <h2 className="font-display font-extrabold text-base uppercase">{title}</h2>}
    {children}
  </section>
);
export const Btn = ({ children, variant = "primary", className = "", ...p }) => (
  <button {...p} className={`rounded-2xl px-4 py-3 text-sm font-bold active:scale-[0.97] transition disabled:opacity-40 ${variant === "primary" ? "bg-cyan-400 text-zinc-950" : variant === "danger" ? "bg-red-500/15 text-red-500 border border-red-500/30" : "bg-slate-100 border border-slate-200 text-slate-800"} ${className}`}>{children}</button>
);
export const Field = ({ label, children }) => (
  <label className="block"><span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">{label}</span><div className="mt-1">{children}</div></label>
);
const Row = ({ p, children, testid }) => (
  <div data-testid={testid} className="flex items-center gap-2.5 rounded-2xl bg-slate-50 px-3 py-2">
    <Avatar user={p} className="w-8 h-8 text-[10px]" />
    <span className="flex-1 text-sm font-semibold truncate">{p.short_name}<span className="num text-[10px] text-slate-500 ml-1.5">{p.primary_position}</span></span>
    {children}
  </div>
);
const Icon = ({ children, ...p }) => <button {...p} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center active:scale-90">{children}</button>;

export const MatchSection = ({ data, act }) => {
  const m = data.match;
  const [f, setF] = useState({});
  useEffect(() => setF({ date: m.date, time: m.time, location: m.location, max_players: m.max_players, substitutes: m.substitutes || 0, registration_mode: m.registration_mode }), [m]);
  return (
    <Card title="Match" testid="admin-match-section">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Date"><input data-testid="admin-match-date" type="date" className="gg-input" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Heure"><input data-testid="admin-match-time" type="time" className="gg-input" value={f.time || ""} onChange={(e) => setF({ ...f, time: e.target.value })} /></Field>
      </div>
      <Field label="Lieu"><input data-testid="admin-match-location" className="gg-input" value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Capacité"><input data-testid="admin-match-capacity" type="number" className="gg-input" value={f.max_players ?? ""} onChange={(e) => setF({ ...f, max_players: +e.target.value })} /></Field>
        <Field label="Remplaçants"><input data-testid="admin-match-subs" type="number" className="gg-input" value={f.substitutes ?? ""} onChange={(e) => setF({ ...f, substitutes: +e.target.value })} /></Field>
      </div>
      <Field label="Inscriptions">
        <select data-testid="admin-registration-mode" className="gg-input" value={f.registration_mode || "auto"} onChange={(e) => setF({ ...f, registration_mode: e.target.value })}>
          <option value="auto">Automatique (ouverture programmée)</option>
          <option value="open">Ouvertes</option>
          <option value="closed">Fermées</option>
        </select>
      </Field>
      <Btn data-testid="admin-match-save" className="w-full" onClick={() => act("put", "", f, "Match mis à jour")}>Enregistrer</Btn>
      <Btn data-testid="admin-match-cancel" variant="danger" className="w-full" onClick={() => window.confirm("Annuler ce match ? Le mardi suivant sera préparé automatiquement.") && act("post", "/cancel", null, "Match annulé")}>Annuler le match</Btn>
    </Card>
  );
};

export const RegistrationSection = ({ data, act }) => {
  const [users, setUsers] = useState([]);
  const [add, setAdd] = useState("");
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const inMatch = new Set(data.players.map((p) => p.id));
  const confirmed = data.players.filter((p) => p.registration_status === "confirmed");
  const waiting = data.players.filter((p) => p.registration_status === "waiting_list").sort((a, b) => a.waiting_list_position - b.waiting_list_position);
  const remove = (p) => window.confirm(`Retirer ${p.short_name} du match ?`) && act("delete", `/players/${p.id}`, undefined, "Joueur retiré");
  return (
    <Card title={`Inscriptions · ${confirmed.length} / ${data.capacity}`} testid="admin-registrations-section">
      <div className="flex gap-2">
        <select data-testid="admin-add-player-select" className="gg-input flex-1" value={add} onChange={(e) => setAdd(e.target.value)}>
          <option value="">Ajouter un joueur…</option>
          {users.filter((u) => !inMatch.has(u.id)).map((u) => <option key={u.id} value={u.id}>{u.short_name}</option>)}
        </select>
        <Btn data-testid="admin-add-player-button" disabled={!add} onClick={() => act("post", "/players", { user_id: add }, "Joueur ajouté").then(() => setAdd(""))}>Ajouter</Btn>
      </div>
      {waiting.length > 0 && <p className="text-xs font-bold text-amber-600 uppercase">Liste d'attente</p>}
      {waiting.map((p, i) => (
        <Row key={p.id} p={p} testid={`admin-waiting-${p.id}`}>
          <span className="num text-xs text-slate-500">{i + 1}</span>
          <Icon data-testid={`admin-wait-up-${p.id}`} onClick={() => act("post", "/waitlist/move", { user_id: p.id, direction: "up" })}><ArrowUp className="w-4 h-4" /></Icon>
          <Icon data-testid={`admin-wait-down-${p.id}`} onClick={() => act("post", "/waitlist/move", { user_id: p.id, direction: "down" })}><ArrowDown className="w-4 h-4" /></Icon>
          <Icon data-testid={`admin-promote-${p.id}`} onClick={() => act("post", "/promote", { user_id: p.id }, "Joueur promu")}><Check className="w-4 h-4 text-cyan-600" /></Icon>
          <Icon data-testid={`admin-remove-${p.id}`} onClick={() => remove(p)}><X className="w-4 h-4 text-red-500" /></Icon>
        </Row>
      ))}
      <p className="text-xs font-bold text-slate-500 uppercase">Confirmés</p>
      <div className="space-y-1.5 max-h-80 overflow-y-auto">
        {confirmed.map((p) => (
          <Row key={p.id} p={p} testid={`admin-confirmed-${p.id}`}>
            <Icon data-testid={`admin-remove-${p.id}`} onClick={() => remove(p)}><X className="w-4 h-4 text-red-500" /></Icon>
          </Row>
        ))}
      </div>
    </Card>
  );
};

const P12 = ["1", ...Array(30).fill("2")].join("-");
const MODES = [["1", "Mode 1 - 1"], ["2", "Mode 2 - 2"], [P12, "Mode 1 - 2 - 2 - 2…"], ["custom", "Séquence personnalisée"]];
const PRESETS = MODES.slice(0, 3).map(([k]) => k);

export const DraftSection = ({ data, act }) => {
  const m = data.match;
  const confirmed = data.players.filter((p) => p.registration_status === "confirmed");
  const cur = (m.draft.pattern || [1]).join("-");
  const [mode, setMode] = useState(PRESETS.includes(cur) ? cur : "custom");
  const [custom, setCustom] = useState(cur);
  const pattern = (mode === "custom" ? custom : mode).split(/[^0-9]+/).map(Number).filter((n) => n > 0);
  const first = data.draft.first || "white";
  return (
    <Card title="Capitaines & draft" testid="admin-draft-section">
      {["white", "black"].map((t) => (
        <Field key={t} label={t === "white" ? "Capitaine Blanc" : "Capitaine Noir"}>
          <select data-testid={`admin-captain-${t}`} className="gg-input" value={m[`captain_${t}`] || ""} disabled={m.teams_locked}
            onChange={(e) => e.target.value && act("put", "/captains", { team: t, user_id: e.target.value }, "Capitaine nommé")}>
            <option value="">— Choisir —</option>
            {confirmed.map((p) => <option key={p.id} value={p.id}>{p.short_name}</option>)}
          </select>
        </Field>
      ))}
      <Field label="Mode de sélection">
        <div className="grid grid-cols-2 gap-1.5">
          {MODES.map(([k, l]) => (
            <button key={k} data-testid={`admin-draft-mode-${k === P12 ? "1-2-2" : k}`} onClick={() => setMode(k)} className={`rounded-xl py-2 text-xs font-bold border ${mode === k ? "bg-cyan-400 text-zinc-950 border-cyan-300" : "bg-slate-50 border-slate-200"}`}>{l}</button>
          ))}
        </div>
      </Field>
      {mode === "custom" && <input data-testid="admin-draft-custom" className="gg-input" placeholder="ex : 1-2-2-2-2-1" value={custom} onChange={(e) => setCustom(e.target.value)} />}
      <Field label="Qui choisit en premier ?">
        <div className="grid grid-cols-2 gap-1.5">
          {[["white", "Capitaine Blanc"], ["black", "Capitaine Noir"]].map(([t, l]) => (
            <button key={t} data-testid={`admin-draft-first-${t}`} disabled={m.teams_locked} onClick={() => t !== first && act("put", "/draft/first", { first: t }, `${l} commence`)}
              className={`rounded-xl py-2 text-xs font-bold border ${first === t ? "bg-cyan-400 text-zinc-950 border-cyan-300" : "bg-slate-50 border-slate-200"}`}>{l}</button>
          ))}
        </div>
      </Field>
      <p className="text-xs text-slate-500" data-testid="admin-draft-sequence">{first === "white" ? "Blanc" : "Noir"} commence. {mode === P12 ? "Séquence : 1 - 2 - 2 - 2 - 2 - 2… (le 1er capitaine choisit 1 joueur, puis chacun 2 à tour de rôle)" : `Séquence : ${pattern.join(" - ") || "—"} (répétée jusqu'à la fin)`}</p>
      <p className="text-xs text-slate-500" data-testid="admin-draft-status">{data.draft.complete ? "Draft terminé" : data.draft.started ? `Draft en cours · ${data.draft.remaining} joueur(s) restant(s)` : "Le draft démarre automatiquement dès que les deux capitaines sont nommés"}</p>
      <div className="grid grid-cols-2 gap-2">
        <Btn data-testid="admin-draft-start" disabled={!pattern.length || m.teams_locked} onClick={() => act("post", "/draft/start", { pattern }, "Draft lancé")}>{data.draft.started ? "Mettre à jour" : "Démarrer"}</Btn>
        <Btn data-testid="admin-draft-reset" variant="secondary" disabled={m.teams_locked} onClick={() => window.confirm("Réinitialiser le draft ? Les équipes seront vidées (sauf capitaines).") && act("post", "/draft/reset", null, "Draft réinitialisé")}>Réinitialiser</Btn>
      </div>
      {data.draft.started && <Link to="/matchs" className="block text-center text-xs text-cyan-600 underline">Ouvrir l'écran de draft</Link>}
    </Card>
  );
};

export const TeamsSection = ({ data, act }) => {
  const m = data.match;
  const confirmed = data.players.filter((p) => p.registration_status === "confirmed");
  return (
    <Card title="Équipes" testid="admin-teams-section">
      <div className="grid grid-cols-2 gap-2">
        <Btn data-testid="admin-auto-lineup" variant="secondary" onClick={() => act("post", "/lineup/auto", null, "Placement 4-4-2 recalculé")}>Placement auto</Btn>
        <Btn data-testid="admin-lock-toggle" onClick={() => act("post", "/lock", { locked: !m.teams_locked }, m.teams_locked ? "Équipes déverrouillées" : "Équipes verrouillées")}>
          {m.teams_locked ? "DÉVERROUILLER" : "VERROUILLER"}
        </Btn>
      </div>
      <p className="text-xs text-slate-500">Pour changer un poste ou échanger deux joueurs, touchez un joueur sur le terrain (onglet Matchs).</p>
      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {confirmed.map((p) => (
          <Row key={p.id} p={p} testid={`admin-team-row-${p.id}`}>
            {p.is_captain ? <span className="text-[10px] font-black bg-amber-400 text-zinc-950 rounded px-1.5">C</span> : (
              <select data-testid={`admin-team-select-${p.id}`} className="bg-slate-100 rounded-lg text-xs px-2 py-1.5" value={p.team || ""} onChange={(e) => act("post", "/teams/move", { user_id: p.id, team: e.target.value || null })}>
                <option value="">—</option><option value="white">Blanc</option><option value="black">Noir</option>
              </select>
            )}
          </Row>
        ))}
      </div>
    </Card>
  );
};

export const ResultSection = ({ data, act }) => {
  const m = data.match;
  const [s, setS] = useState({ white_score: m.white_score ?? 0, black_score: m.black_score ?? 0 });
  return (
    <Card title="Résultat" testid="admin-result-section">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Score Blanc"><input data-testid="admin-score-white" type="number" min="0" className="gg-input num text-center text-2xl" value={s.white_score} onChange={(e) => setS({ ...s, white_score: +e.target.value })} /></Field>
        <Field label="Score Noir"><input data-testid="admin-score-black" type="number" min="0" className="gg-input num text-center text-2xl" value={s.black_score} onChange={(e) => setS({ ...s, black_score: +e.target.value })} /></Field>
      </div>
      <p className="text-xs text-slate-500">Le score est calculé automatiquement à partir des buts saisis sur le terrain. Utilisez ce formulaire uniquement pour le corriger.</p>
      <Btn data-testid="admin-score-save" className="w-full" onClick={() => act("put", "/score", s, "Score corrigé")}>Corriger le score</Btn>
      <ScoreBoard data={data} />
      <Btn data-testid="admin-complete-match" variant="secondary" className="w-full"
        onClick={() => window.confirm("Terminer le match ? Les points et statistiques seront calculés et le vote Homme du Match sera ouvert.") && act("post", "/complete", null, "Match terminé ! Vote Homme du Match ouvert.")}>
        TERMINER LE MATCH
      </Btn>
      <Link to="/matchs" className="block text-center text-xs text-cyan-600 underline">Corriger buts et passes depuis le terrain</Link>
    </Card>
  );
};
